import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { AssessRiskDialog } from "../../../../components/dashboard/hse/risk/AssessRiskDialog";
import { emptyRiskForm, RISK_PATHS, riskApiError } from "../../../../components/dashboard/hse/risk/riskUtils";
import {
  createRiskAssessment,
  excludeRiskAssessment,
  fetchRiskAssessment,
  fetchRiskCatalog,
  fetchRiskTiltak,
  linkRiskTiltak,
  suggestRiskAssessments,
  updateRiskAssessment,
} from "../../../../api/hse/risk";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

export const HseKsRiskEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id;
  const campaignId = searchParams.get("campaign_id") || "";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(isNew ? "choose" : "assess");
  const [themes, setThemes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [themeId, setThemeId] = useState("");
  const [form, setForm] = useState(emptyRiskForm());
  const [linkedTiltak, setLinkedTiltak] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(!isNew);
  const [suggestForm, setSuggestForm] = useState({ industry: "", fag: "" });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchRiskCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setThemes(catalog.themes || []);
        setCategories(catalog.categories || []);
      })
      .catch(() => {
        if (!cancelled) {
          setThemes([]);
          setCategories([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const record = await fetchRiskAssessment(id);
        if (cancelled) return;
        setForm(record);
        const tiltak = await fetchRiskTiltak(id).catch(() => record.tiltak || []);
        if (!cancelled) setLinkedTiltak(tiltak);
        setDialogOpen(true);
      } catch (err) {
        if (!cancelled) setError(riskApiError(err) || t("hse.risk.load_failed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, t]);

  const openAssess = (next) => {
    setForm(emptyRiskForm(next));
    setDialogOpen(true);
    setMode("assess");
  };

  const persist = async (values, { excluded, stay } = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...values,
        hse_risk_campaign_id: values.hse_risk_campaign_id || campaignId,
        skip_assessment: Boolean(excluded),
        status: excluded ? "skipped" : values.risk_level ? "assessed" : values.status || "draft",
      };
      const saved = (isNew && !form.id)
        ? await createRiskAssessment(payload)
        : excluded
          ? await excludeRiskAssessment(id || form.id, values.skip_reason || values.excluded_reason)
          : await updateRiskAssessment(id || form.id, payload);
      toast.success(excluded ? t("hse.risk.excluded_saved") : isNew && !form.id ? t("hse.risk.created") : t("hse.risk.updated"));
      if (saved?.id) setForm((current) => ({ ...current, ...saved, id: saved.id }));
      if (!stay) navigate(`/dashboard/hse-ks/risk/${saved.id || id}`);
      return saved;
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleLinkTiltak = async (tiltakId, created) => {
    if (created) {
      setLinkedTiltak((current) => [...current, created]);
      return;
    }
    if (!id && !form.id) {
      toast.error(t("hse.risk.tiltak.save_first"));
      return;
    }
    try {
      const riskId = id || form.id;
      await linkRiskTiltak(riskId, tiltakId);
      const tiltak = await fetchRiskTiltak(riskId);
      setLinkedTiltak(tiltak);
      toast.success(t("hse.risk.tiltak.linked"));
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.tiltak.save_failed"));
    }
  };

  const handleSuggest = async (event) => {
    event.preventDefault();
    if (!suggestForm.industry.trim() && !suggestForm.fag.trim()) {
      toast.error(t("hse.risk.suggest.error_industry"));
      return;
    }
    setSuggesting(true);
    try {
      const response = await suggestRiskAssessments({
        industry: suggestForm.industry,
        fag: suggestForm.fag,
        count: 5,
        hse_risk_campaign_id: campaignId,
      });
      setSuggestions(response.data || []);
      if (!(response.data || []).length) {
        toast.error(t("hse.risk.suggest.empty"));
      }
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.suggest.failed"));
    } finally {
      setSuggesting(false);
    }
  };

  const selectedTheme = themes.find((theme) => theme.id === themeId);

  return (
    <HsePageChrome
      title={isNew ? t("hse.risk.new") : t("hse.risk.edit")}
      subtitle={t("hse.risk.form_subtitle")}
      backTo={
        isNew
          ? campaignId
            ? RISK_PATHS.campaign(campaignId)
            : RISK_PATHS.hub
          : `/dashboard/hse-ks/risk/${id}`
      }
    >
      <div className="mx-auto mt-8 max-w-3xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <ResourceError errors={[error]} />
        ) : (
          <div className="space-y-4">
            {isNew && mode === "choose" ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setMode("catalog")}
                  className="rounded-[18px] border border-slate-200 bg-white p-5 text-left shadow-sm hover:shadow-md"
                >
                  <h2 className="text-base font-extrabold text-slate-900">{t("hse.risk.add.common")}</h2>
                  <p className="mt-1 text-sm text-slate-500">{t("hse.risk.add.common_hint")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("custom")}
                  className="rounded-[18px] border border-slate-200 bg-white p-5 text-left shadow-sm hover:shadow-md"
                >
                  <h2 className="text-base font-extrabold text-slate-900">{t("hse.risk.add.custom")}</h2>
                  <p className="mt-1 text-sm text-slate-500">{t("hse.risk.add.custom_hint")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("suggest")}
                  className="rounded-[18px] border border-slate-200 bg-white p-5 text-left shadow-sm hover:shadow-md"
                >
                  <h2 className="text-base font-extrabold text-slate-900">{t("hse.risk.add.suggest")}</h2>
                  <p className="mt-1 text-sm text-slate-500">{t("hse.risk.add.suggest_hint")}</p>
                </button>
              </div>
            ) : null}

            {mode === "catalog" ? (
              <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-700">{t("hse.risk.add.pick_theme")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {themes.map((theme) => (
                    <button
                      key={theme.id}
                      type="button"
                      onClick={() => setThemeId(theme.id)}
                      className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                        themeId === theme.id
                          ? "bg-[#1463C7] text-white"
                          : "border border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {theme.title || t(`hse.risk.theme.${theme.id}`, { defaultValue: theme.id })}
                    </button>
                  ))}
                </div>
                {selectedTheme ? (
                  <ul className="mt-5 divide-y divide-slate-100">
                    {selectedTheme.challenges.map((challenge) => (
                      <li key={challenge.id}>
                        <button
                          type="button"
                          onClick={() =>
                            openAssess({
                              title: challenge.title || t(`hse.risk.catalog.${challenge.id}`, { defaultValue: challenge.id }),
                              category: challenge.category || selectedTheme.category,
                              theme: selectedTheme.id,
                              catalog_key: challenge.id,
                              challenge_key: challenge.id,
                              challenge_title: challenge.title,
                              challenge_kind: "common",
                              consequence: challenge.consequence || "",
                              hse_risk_campaign_id: campaignId,
                            })
                          }
                          className="w-full py-3 text-left hover:bg-slate-50"
                        >
                          <p className="text-sm font-bold text-slate-900">
                            {challenge.title || t(`hse.risk.catalog.${challenge.id}`, { defaultValue: challenge.id })}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {mode === "custom" ? (
              <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-700">{t("hse.risk.add.pick_category")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(categories.length
                    ? categories
                    : [{ id: "", slug: "", name: t("hse.risk.add.custom") }]
                  ).map((category) => (
                    <button
                      key={category.id || "custom"}
                      type="button"
                      onClick={() =>
                        openAssess({
                          challenge_kind: "custom",
                          hse_risk_category_id: category.id,
                          category: category.slug,
                          category_name: category.name,
                          title: "",
                          hse_risk_campaign_id: campaignId,
                        })
                      }
                      className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {mode === "suggest" ? (
              <div className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-sm">
                <form onSubmit={handleSuggest} className="space-y-4">
                  <p className="text-sm font-semibold text-slate-700">{t("hse.risk.suggest.hint")}</p>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.suggest.industry")}</span>
                      <input
                        className={inputClass}
                        value={suggestForm.industry}
                        onChange={(event) =>
                          setSuggestForm((current) => ({ ...current, industry: event.target.value }))
                        }
                      />
                    </label>
                    <label className="block">
                      <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.suggest.fag")}</span>
                      <input
                        className={inputClass}
                        value={suggestForm.fag}
                        onChange={(event) =>
                          setSuggestForm((current) => ({ ...current, fag: event.target.value }))
                        }
                      />
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={suggesting}
                    className="rounded-full bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    {suggesting ? t("hse.risk.suggest.working") : t("hse.risk.suggest.run")}
                  </button>
                </form>
                {suggestions.length > 0 ? (
                  <ul className="mt-5 divide-y divide-slate-100">
                    {suggestions.map((item) => (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/hse-ks/risk/${item.id}/edit`)}
                          className="w-full py-3 text-left hover:bg-slate-50"
                        >
                          <p className="text-sm font-bold text-slate-900">{item.title}</p>
                          <p className="text-xs text-slate-500">{item.consequence || item.description}</p>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <AssessRiskDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          if (!isNew) navigate(`/dashboard/hse-ks/risk/${id}`);
        }}
        initialValues={form}
        saving={saving}
        linkedTiltak={linkedTiltak}
        categories={categories}
        onSave={(values) => persist(values)}
        onExclude={(values) => persist(values, { excluded: true })}
        onLinkTiltak={handleLinkTiltak}
        onEnsureSaved={async (values) => persist({ ...values, status: "draft" }, { stay: true })}
      />
    </HsePageChrome>
  );
};
